import { ref, computed, onMounted } from 'vue'
import { api } from '@/plugins/api'

interface PushSubscriptionInfo {
  id: string
  deviceName: string
  userAgent?: string
  lastUsedAt?: string
  createdAt: string
}

const isSupported = ref(false)
const isEnabled = ref(false)
const permission = ref<NotificationPermission>('default')
const subscription = ref<PushSubscription | null>(null)
const subscriptions = ref<PushSubscriptionInfo[]>([])
const vapidPublicKey = ref<string | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

// Check if push notifications are supported
function checkSupport(): boolean {
  if (!('serviceWorker' in navigator)) {
    console.log('[Push] Service Worker not supported')
    return false
  }
  if (!('PushManager' in window)) {
    console.log('[Push] Push API not supported')
    return false
  }
  if (!('Notification' in window)) {
    console.log('[Push] Notifications not supported')
    return false
  }
  return true
}

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Get device name from user agent
function getDeviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  if (/Mac/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Linux/.test(ua)) return 'Linux'
  return 'Unknown Device'
}

export function usePushNotifications() {
  const canEnable = computed(() => {
    return isSupported.value && vapidPublicKey.value && permission.value !== 'denied'
  })

  const needsPermission = computed(() => {
    return permission.value === 'default'
  })

  // Initialize push notifications
  async function init() {
    isSupported.value = checkSupport()
    if (!isSupported.value) return

    permission.value = Notification.permission

    try {
      // Get VAPID public key from server
      const response = await api.get('/push/vapid-public-key')
      vapidPublicKey.value = response.data.publicKey
      isEnabled.value = response.data.enabled

      if (!isEnabled.value) {
        console.log('[Push] Push notifications not enabled on server')
        return
      }

      // Register service worker if not already registered
      const registration = await navigator.serviceWorker.ready

      // Check if already subscribed
      subscription.value = await registration.pushManager.getSubscription()

      // Load user's subscriptions list
      await loadSubscriptions()
    } catch (e) {
      console.error('[Push] Init error:', e)
      error.value = 'Не удалось инициализировать push-уведомления'
    }
  }

  // Request notification permission
  async function requestPermission(): Promise<boolean> {
    if (!isSupported.value) return false

    try {
      const result = await Notification.requestPermission()
      permission.value = result
      return result === 'granted'
    } catch (e) {
      console.error('[Push] Permission request error:', e)
      return false
    }
  }

  // Subscribe to push notifications
  async function subscribe(): Promise<boolean> {
    if (!canEnable.value || !vapidPublicKey.value) return false

    loading.value = true
    error.value = null

    try {
      // Request permission if needed
      if (permission.value !== 'granted') {
        const granted = await requestPermission()
        if (!granted) {
          error.value = 'Разрешение на уведомления не получено'
          return false
        }
      }

      // Subscribe with PushManager
      const registration = await navigator.serviceWorker.ready
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey.value)
      })

      // Send subscription to server
      const subscriptionJson = pushSubscription.toJSON()
      await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: {
          p256dh: subscriptionJson.keys?.p256dh,
          auth: subscriptionJson.keys?.auth
        },
        deviceName: getDeviceName()
      })

      subscription.value = pushSubscription
      await loadSubscriptions()

      return true
    } catch (e: unknown) {
      console.error('[Push] Subscribe error:', e)
      error.value = e instanceof Error ? e.message : 'Не удалось подписаться на уведомления'
      return false
    } finally {
      loading.value = false
    }
  }

  // Unsubscribe from push notifications
  async function unsubscribe(): Promise<boolean> {
    if (!subscription.value) return true

    loading.value = true
    error.value = null

    try {
      // Unsubscribe from PushManager
      await subscription.value.unsubscribe()

      // Remove from server
      await api.delete('/push/unsubscribe', {
        data: { endpoint: subscription.value.endpoint }
      })

      subscription.value = null
      await loadSubscriptions()

      return true
    } catch (e: unknown) {
      console.error('[Push] Unsubscribe error:', e)
      error.value = e instanceof Error ? e.message : 'Не удалось отписаться от уведомлений'
      return false
    } finally {
      loading.value = false
    }
  }

  // Unsubscribe all devices
  async function unsubscribeAll(): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      // Unsubscribe current device first
      if (subscription.value) {
        await subscription.value.unsubscribe()
        subscription.value = null
      }

      // Remove all from server
      await api.delete('/push/subscriptions')
      subscriptions.value = []

      return true
    } catch (e: unknown) {
      console.error('[Push] Unsubscribe all error:', e)
      error.value = e instanceof Error ? e.message : 'Не удалось отписать все устройства'
      return false
    } finally {
      loading.value = false
    }
  }

  // Load user's subscriptions list
  async function loadSubscriptions() {
    try {
      const response = await api.get('/push/subscriptions')
      subscriptions.value = response.data
    } catch (e) {
      console.error('[Push] Load subscriptions error:', e)
    }
  }

  // Check if current browser is subscribed
  const isCurrentDeviceSubscribed = computed(() => {
    return subscription.value !== null
  })

  onMounted(() => {
    init()
  })

  return {
    isSupported,
    isEnabled,
    permission,
    subscription,
    subscriptions,
    loading,
    error,
    canEnable,
    needsPermission,
    isCurrentDeviceSubscribed,
    init,
    requestPermission,
    subscribe,
    unsubscribe,
    unsubscribeAll,
    loadSubscriptions
  }
}
